import { useCallback, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { cn } from "@/zapi-frontend/shared/lib/utils";

type TSlideOutlineEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isLabs: boolean;
};

export function SlideOutlineEditor(props: TSlideOutlineEditorProps) {
  const { value, onChange, placeholder = "• Key point 1\n• Key point 2\n• Key point 3", isLabs } = props;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        hardBreak: false
      }),
      Placeholder.configure({ placeholder })
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: cn(
          "text-innoveraZinc-700 w-full h-full resize-none border-none outline-none px-2 py-1 bg-transparent focus:outline-none max-w-none",
          isLabs ? "text-base prose prose-sm" : "text-lg prose prose-base"
        )
      }
    }
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) editor.commands.setContent(value);
  }, [value, editor]);

  const ToolbarButton = useCallback(
    ({
      isActive,
      onClick,
      children,
      title
    }: {
      isActive: boolean;
      onClick: () => void;
      children: React.ReactNode;
      title: string;
    }) => (
      <button
        type="button"
        onClick={onClick}
        title={title}
        className={cn(
          "p-1.5 rounded text-sm font-medium transition-colors",
          isActive
            ? "bg-innoveraOrange-100 text-innoveraOrange-600"
            : "text-innoveraZinc-500 hover:bg-innoveraZinc-100 hover:text-innoveraZinc-700"
        )}
      >
        {children}
      </button>
    ),
    []
  );

  if (!editor) return null;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-1 pb-2 border-b border-innoveraZinc-150 mb-2">
        <ToolbarButton
          isActive={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold (Ctrl+B)"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z" />
          </svg>
        </ToolbarButton>

        <ToolbarButton
          isActive={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic (Ctrl+I)"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4h-8z" />
          </svg>
        </ToolbarButton>

        <div className="w-px h-4 bg-innoveraZinc-200 mx-1" />

        <ToolbarButton
          isActive={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet List"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z" />
          </svg>
        </ToolbarButton>

        <ToolbarButton
          isActive={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numbered List"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z" />
          </svg>
        </ToolbarButton>
      </div>

      <div className="flex-1 overflow-auto">
        <EditorContent
          editor={editor}
          className={cn(
            "h-full [&_.ProseMirror]:h-full [&_.ProseMirror]:focus:outline-none [&_.ProseMirror_p]:leading-relaxed [&_.ProseMirror_li]:leading-relaxed [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-innoveraZinc-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5",
            isLabs
              ? "[&_.ProseMirror]:text-base [&_.ProseMirror_p]:text-base [&_.ProseMirror_strong]:text-base [&_.ProseMirror_li]:text-base"
              : "[&_.ProseMirror]:text-lg [&_.ProseMirror_p]:text-lg [&_.ProseMirror_strong]:text-lg [&_.ProseMirror_li]:text-lg"
          )}
        />
      </div>
    </div>
  );
}
